import { Box, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/system';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { MdBarChart, MdSportsSoccer } from 'react-icons/md';
import Card from '../../../components/card/Card';
import { translate } from '../../../i18n';
import { useChartOptions } from '../../../hooks/useChartOptions';
import { GameRankedMode, GameType } from '../../../models/game/dto/game.dto';
import { classifyRead } from '../../_shared/readState';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { getDemoGameCountByType, isDemoOrganization } from '../demoAnalyticsData';
import { AnalyticsFilterParams } from '../hooks/analyticsFilterParams';
import { useGetGameCountByType } from '../hooks/useGetGameCountByType';
import ChartReadState, { chartHasSomethingToDraw } from './ChartReadState';

const SEGMENT_COLORS: Record<string, string> = {
    [`${GameType.PUBLIC}:${GameRankedMode.RANKED}`]: '#4318FF',
    [`${GameType.PRIVATE}:${GameRankedMode.RANKED}`]: '#7551FF',
    [`${GameType.PUBLIC}:${GameRankedMode.UNRANKED}`]: '#0EA5E9',
    [`${GameType.PRIVATE}:${GameRankedMode.UNRANKED}`]: '#67E8F9',
};

const SEGMENT_LABELS: Record<string, () => string> = {
    [`${GameType.PUBLIC}:${GameRankedMode.RANKED}`]: () => translate('analytics.ranked_public'),
    [`${GameType.PRIVATE}:${GameRankedMode.RANKED}`]: () => translate('analytics.ranked_private'),
    [`${GameType.PUBLIC}:${GameRankedMode.UNRANKED}`]: () => translate('analytics.unranked_public'),
    [`${GameType.PRIVATE}:${GameRankedMode.UNRANKED}`]: () => translate('analytics.unranked_private'),
};

// Canonical ordering for consistent display
const SEGMENT_ORDER = [
    `${GameType.PUBLIC}:${GameRankedMode.RANKED}`,
    `${GameType.PRIVATE}:${GameRankedMode.RANKED}`,
    `${GameType.PUBLIC}:${GameRankedMode.UNRANKED}`,
    `${GameType.PRIVATE}:${GameRankedMode.UNRANKED}`,
];

type Props = {
    startDate: Date;
    endDate: Date;
    filters?: AnalyticsFilterParams;
};

const GameTypeDistributionChart = ({ startDate, endDate, filters }: Props) => {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const orgId = selectedOrganization?.id;
    const isDemo = isDemoOrganization(orgId);
    const { data: realData, isInitialLoading, isError, refetch } = useGetGameCountByType(
        selectedLocation?.id,
        startDate,
        endDate,
        filters,
    );

    const chartData = useMemo(() => {
        if (isDemo) return getDemoGameCountByType(orgId);
        return realData;
    }, [isDemo, realData, orgId]);

    /**
     * This component took NO error flag before this run, and both non-answers
     * landed on a definitive claim about the organization's games:
     *
     * - REFUSED (`data === undefined`, not loading) fell into `!hasData &&
     *   !isLoading` and rendered "No games yet — nobody has played here yet",
     *   about a read the API refused. Every `analytics/*` route carries
     *   `@UseGuards(AdminAuthGuard)`, so a stale token or a revoked admin
     *   reaches this with no outage at all.
     * - NEVER ISSUED (the query is gated `enabled: !!locationId`) reported
     *   `isLoading: true` forever, so it fell the OTHER way — to the donut,
     *   drawn from an empty series with its centre label formatted from
     *   `total`, i.e. a permanent, confident "Games 0".
     *
     * The demo org substitutes its own counts and is `answered` by
     * construction.
     */
    const readState = isDemo ? 'answered' : classifyRead(isError, isInitialLoading, realData);

    const hasData = chartData && chartData.items.length > 0;
    const total = useMemo(() => (chartData?.items ?? []).reduce((sum, item) => sum + item.count, 0), [chartData]);

    const { series, labels, colors } = useMemo(() => {
        if (!chartData?.items.length) return { series: [], labels: [], colors: [] };

        const itemMap = new Map(
            chartData.items.map((item) => [`${item.gameType}:${item.rankedMode}`, item.count]),
        );

        const orderedItems = SEGMENT_ORDER
            .filter((key) => itemMap.has(key))
            .map((key) => ({
                key,
                count: itemMap.get(key)!,
            }));

        return {
            series: orderedItems.map((i) => i.count),
            labels: orderedItems.map((i) => SEGMENT_LABELS[i.key]()),
            colors: orderedItems.map((i) => SEGMENT_COLORS[i.key]),
        };
    }, [chartData]);

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400');

    const baseChart = useChartOptions();
    const options: ApexCharts.ApexOptions = {
        ...baseChart,
        chart: {
            ...baseChart.chart,
            type: 'donut',
        },
        labels,
        colors,
        legend: {
            ...baseChart.legend,
            position: 'bottom',
            fontSize: '13px',
            labels: { colors: textColor },
        },
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: translate('analytics.games'),
                            fontSize: '14px',
                            color: textColor,
                            formatter: () => String(total),
                        },
                        value: {
                            fontSize: '24px',
                            fontWeight: '700',
                            color: textColor,
                        },
                    },
                },
            },
        },
        tooltip: {
            y: {
                formatter: (val: number) => {
                    const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
                    return `${val} (${pct}%)`;
                },
            },
        },
        stroke: {
            show: false,
        },
    };

    return (
        <Card alignItems="center" flexDirection="column" w="100%">
            <Flex align="center" w="100%" px="15px" py="10px">
                <Text
                    me="auto"
                    color={textColor}
                    fontSize="xl"
                    fontWeight="700"
                    lineHeight="100%"
                >
                    {translate('analytics.game_type_distribution')}
                </Text>
            </Flex>

            <Box h="350px" mt="auto" w="100%" display="flex" alignItems="center" justifyContent="center">
                {!chartHasSomethingToDraw(readState) ? (
                    <ChartReadState state={readState} gate='location' onRetry={() => { refetch(); }} />
                ) : !hasData && readState !== 'loading' ? (
                    // Reached only once the read has ANSWERED: an organization
                    // that genuinely played no games, or none matching the
                    // filters. That claim is the component's to make here and
                    // nowhere else.
                    <VStack spacing={2}>
                        <Icon as={MdSportsSoccer} w="40px" h="40px" color={textColorSecondary} />
                        <Text color={textColorSecondary} fontSize="md" fontWeight="600">
                            {total === 0 && !filters
                                ? translate('analytics.no_games_yet')
                                : translate('analytics.no_games_match_filters')}
                        </Text>
                        <Text color={textColorSecondary} fontSize="sm">
                            {total === 0 && !filters
                                ? translate('analytics.no_games_yet_subtitle')
                                : translate('analytics.no_games_match_filters_subtitle')}
                        </Text>
                    </VStack>
                ) : (
                    <ReactApexChart
                        key={`game-type-dist-${isDemo}`}
                        options={options}
                        series={series}
                        type="donut"
                        height={350}
                        width="100%"
                    />
                )}
            </Box>
        </Card>
    );
};

export default GameTypeDistributionChart;
